import { ref, type Ref } from 'vue';
import {
  getParentId,
  isDescendant,
  isFolderLikeKind,
  isMovableNode,
  normalizeMoveNodeIds,
} from './directoryTree.utils';
import type { DirectoryTreeNode } from './types';

interface UseDirectoryDragAndDropParams {
  tree: Ref<DirectoryTreeNode[]>;
  selectedNodeIds: Ref<string[]>;
  onMove: (itemIds: string[], targetId: string) => Promise<void> | void;
}

export function useDirectoryDragAndDrop(params: UseDirectoryDragAndDropParams) {
  const draggingIds = ref<string[]>([]);
  const dragOverNodeId = ref<string>('');

  const isNodeDraggable = (node: DirectoryTreeNode): boolean => isMovableNode(node);

  const resolveTargetId = (node: DirectoryTreeNode): string | null => {
    if (isFolderLikeKind(node.kind)) {
      return node.id;
    }
    return getParentId(params.tree.value, node.id) ?? null;
  };

  const isValidTarget = (targetId: string | null): boolean => {
    if (!targetId || draggingIds.value.length === 0) {
      return false;
    }

    return draggingIds.value.every((id) => {
      if (id === targetId) {
        return false;
      }
      if (getParentId(params.tree.value, id) === targetId) {
        return false;
      }
      return !isDescendant(params.tree.value, id, targetId);
    });
  };

  const buildDraggingIds = (node: DirectoryTreeNode): string[] => {
    const selected = params.selectedNodeIds.value;
    const base = selected.includes(node.id) ? selected : [node.id];
    return normalizeMoveNodeIds(params.tree.value, base);
  };

  const reset = (): void => {
    draggingIds.value = [];
    dragOverNodeId.value = '';
  };

  const onNodeDragStart = (event: DragEvent, node: DirectoryTreeNode): void => {
    if (!isNodeDraggable(node)) {
      event.preventDefault();
      return;
    }
    draggingIds.value = buildDraggingIds(node);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', draggingIds.value.join(','));
    }
  };

  const onNodeDragOver = (event: DragEvent, node: DirectoryTreeNode): void => {
    const targetId = resolveTargetId(node);
    if (!isValidTarget(targetId)) {
      dragOverNodeId.value = '';
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    dragOverNodeId.value = targetId as string;
  };

  const onNodeDragLeave = (_event: DragEvent, node: DirectoryTreeNode): void => {
    const targetId = resolveTargetId(node);
    if (targetId && dragOverNodeId.value === targetId) {
      dragOverNodeId.value = '';
    }
  };

  const onNodeDrop = async (event: DragEvent, node: DirectoryTreeNode): Promise<void> => {
    event.preventDefault();
    const targetId = resolveTargetId(node);
    const itemIds = [...draggingIds.value];
    const isValid = isValidTarget(targetId);
    reset();
    if (!isValid || !targetId) {
      return;
    }
    await params.onMove(itemIds, targetId);
  };

  const onNodeDragEnd = (): void => {
    reset();
  };

  return {
    draggingIds,
    dragOverNodeId,
    isNodeDraggable,
    onNodeDragStart,
    onNodeDragOver,
    onNodeDragLeave,
    onNodeDrop,
    onNodeDragEnd,
  };
}
